"use client";

import * as React from "react";
import { useMediaQuery } from "../../hooks/use-media-query";
import { Button } from "../button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { CentralizedDrawer } from "../global-drawer-stack";
import { ResponsivePopover } from "./ResponsivePopover";
import { ChevronDown, Check, Clock, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { SelectOption } from "./types";
import { getRecents, addRecent, clearRecents } from "./recents";

interface ResponsiveCommandSelectProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  isSearching?: boolean;
  className?: string;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  // Recents (optional)
  enableRecents?: boolean;
  recentKey?: string; // e.g., "contacts", "tickets"
  recentMax?: number; // default 8
  onRecordRecent?: (item: { id: string; label: string }) => void;
  // Custom option renderer
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;
}

export function ResponsiveCommandSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  disabled = false,
  searchValue,
  onSearchChange,
  isSearching = false,
  className,
  title = "Select Option",
  description,
  searchPlaceholder = "Search options...",
  enableRecents = false,
  recentKey,
  recentMax = 8,
  onRecordRecent,
  customOptionRenderer,
}: ResponsiveCommandSelectProps) {
  const [open, setOpen] = React.useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
  const [internalSearch, setInternalSearch] = React.useState("");
  const [recentsNonce, setRecentsNonce] = React.useState(0);

  // Use internal search state when no onSearchChange handler is provided
  const effectiveSearch = onSearchChange ? (searchValue || "") : internalSearch;

  // Keep focus on input when open
  React.useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, searchValue]);

  // Measure trigger width on open (desktop) to prevent content shrinking while typing
  React.useEffect(() => {
    if (isDesktop && open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(rect.width);
    }
  }, [isDesktop, open]);

  // Compute recents list (filtered by search if provided)
  const recentOptions: SelectOption[] = React.useMemo(() => {
    if (!(enableRecents && recentKey)) return [];
    const recents = getRecents(recentKey);
    const mapped = recents
      .map((r) => ({ value: r.id, label: r.label }))
      .filter((r) => !options.some((o) => o.value === r.value));
    const merged = [...mapped, ...options.filter((o) => recents.some((r) => r.id === o.value))];
    const search = (effectiveSearch || "").toLowerCase();
    return search
      ? merged.filter((o) => o.label.toLowerCase().includes(search))
      : merged;
  }, [enableRecents, recentKey, options, effectiveSearch, recentsNonce]);

  const selected = React.useMemo(() => {
    const inOptions = options.find((o) => o.value === value);
    if (inOptions) return inOptions;
    // Fall back to recents so selection persists when item is moved to recents
    const inRecents = recentOptions.find((o) => o.value === value);
    return inRecents || null;
  }, [options, recentOptions, value]);

  const handleSelect = React.useCallback(
    (id: string) => {
      const opt = options.find((o) => o.value === id);
      if (!opt) return;
      onValueChange(id, opt);
      // Record recent selection if enabled
      if (enableRecents && recentKey) {
        const record = { id, label: opt.label };
        try {
          onRecordRecent ? onRecordRecent(record) : addRecent(recentKey, record, recentMax);
        } catch {
          // no-op
        }
        // Force recents recompute immediately after recording
        setRecentsNonce((n) => n + 1);
      }
      setOpen(false);
      onSearchChange?.("");
    },
    [options, onValueChange, onSearchChange, enableRecents, recentKey, recentMax, onRecordRecent]
  );

  const Trigger = (
    <Button
      type="button"
      variant="outline"
      className={cn("justify-between w-full", className)}
      disabled={disabled}
      ref={triggerRef}
      onClick={() => setOpen((o) => !o)}
    >
      {selected ? (
        <div className="flex-1 text-left">
          <span>{selected.label}</span>
          {selected.description && (
            <span className="ml-1 text-xs text-muted-foreground">({selected.description})</span>
          )}
        </div>
      ) : (
        placeholder
      )}
      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  const hasRecents = enableRecents && recentKey && recentOptions.length > 0;
  const recentValueSet = React.useMemo(() => new Set(recentOptions.map((o) => o.value)), [recentOptions]);

  const List = (
    <Command shouldFilter={false} className="w-full">
      <CommandInput 
        ref={inputRef}
        placeholder={searchPlaceholder} 
        value={effectiveSearch}
        onValueChange={onSearchChange ?? setInternalSearch}
        autoFocus
      />
      <CommandList className="w-full max-h-[60vh] overflow-y-auto">
        <CommandEmpty>{isSearching ? "Searching..." : "No results found."}</CommandEmpty>
        {hasRecents && (
          <CommandGroup className="w-full" heading={<div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-4 w-4" /> Recent</div>}>
            {recentOptions.map((opt) => (
              <CommandItem 
                className="w-full"
                key={`recent-${opt.value}`} 
                value={opt.value} 
                onSelect={handleSelect}
                keywords={[opt.label, ...(opt.searchableText?.split(" ") || [])]}
              >
                <div className="flex items-center w-full">
                  <div className="flex-1">
                    {customOptionRenderer ? customOptionRenderer(opt) : (
                      <>
                        <div>{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                        )}
                      </>
                    )}
                  </div>
                  {opt.value === value && <Check className="ml-2 h-4 w-4" />}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup className="w-full">
          {options
            .filter((opt) => !recentValueSet.has(opt.value))
            .filter((opt) => {
              const s = (effectiveSearch || "").toLowerCase();
              if (!s) return true;
              const hay = `${opt.label} ${(opt.searchableText || "")}`.toLowerCase();
              return hay.includes(s);
            })
            .map((opt) => (
            <CommandItem 
              className="w-full"
              key={opt.value} 
              value={opt.value} 
              onSelect={handleSelect}
              keywords={[opt.label, ...(opt.searchableText?.split(" ") || [])]}
            >
              <div className="flex items-center w-full">
                <div className="flex-1">
                  {customOptionRenderer ? customOptionRenderer(opt) : (
                    <>
                      <div>{opt.label}</div>
                      {opt.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                      )}
                    </>
                  )}
                </div>
                {opt.value === value && <Check className="ml-2 h-4 w-4" />}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
        {enableRecents && recentKey && (
          <div className="flex items-center justify-end p-2">
            <Button
              type="button"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try { clearRecents(recentKey); } catch {}
                setRecentsNonce((n) => n + 1);
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear recents
            </Button>
          </div>
        )}
      </CommandList>
    </Command>
  );

  if (isDesktop) {
    return (
      <ResponsivePopover
        open={open}
        onOpenChange={setOpen}
        content={
          <div
            className="max-h-[60vh] overflow-y-auto"
            style={
              triggerWidth
                ? { width: `${triggerWidth}px`, minWidth: `${triggerWidth}px` }
                : undefined
            }
          >
            {List}
          </div>
        }
        title={title}
        className="p-0 max-w-none"
        align="start"
        asChild
      >
        {Trigger}
      </ResponsivePopover>
    );
  }

  // Mobile drawer UX aligned with legacy MobileSelectDrawer
  return (
    <>
      {Trigger}
      <CentralizedDrawer
        drawerId="responsive-command-select"
        side="bottom"
        height="90vh"
        title={title}
        description={description}
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">
          {/* Search Input */}
          <div className="mb-4">
            <CommandInput
              placeholder={searchPlaceholder}
              value={effectiveSearch}
              onValueChange={onSearchChange ?? setInternalSearch}
            />
          </div>

          {/* Options List */}
          <div className="space-y-1">
              {hasRecents && (
                <>
                  {recentOptions.map((opt) => (
                    <Button
                      key={`recent-${opt.value}`}
                      variant="ghost"
                      className={cn(
                        "w-full justify-start h-auto p-4 rounded-lg transition-all duration-200 transform active:scale-95",
                        opt.value === value && "bg-primary/10 text-primary border border-primary/20",
                        opt.disabled && "opacity-50 cursor-not-allowed",
                        "hover:bg-muted active:bg-muted/80"
                      )}
                      onClick={() => !opt.disabled && handleSelect(opt.value)}
                      disabled={opt.disabled}
                    >
                  <div className="flex items-center w-full">
                    <div className="flex-1 text-left">
                      {customOptionRenderer ? customOptionRenderer(opt) : (
                        <>
                          <div>{opt.label}</div>
                          {opt.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                          )}
                        </>
                      )}
                    </div>
                        {opt.value === value && (
                          <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                        )}
                      </div>
                    </Button>
                  ))}
                </>
              )}
              {options
                .filter((opt) => !recentValueSet.has(opt.value))
                .filter((opt) => {
                  const s = (effectiveSearch || "").toLowerCase();
                  if (!s) return true;
                  const hay = `${opt.label} ${(opt.searchableText || "")}`.toLowerCase();
                  return hay.includes(s);
                })
                .map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  className={cn(
                    "w-full justify-start h-auto p-4 rounded-lg transition-all duration-200 transform active:scale-95",
                    opt.value === value && "bg-primary/10 text-primary border border-primary/20",
                    opt.disabled && "opacity-50 cursor-not-allowed",
                    "hover:bg-muted active:bg-muted/80"
                  )}
                  onClick={() => !opt.disabled && handleSelect(opt.value)}
                  disabled={opt.disabled}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1 text-left">
                      {customOptionRenderer ? customOptionRenderer(opt) : (
                        <>
                          <div>{opt.label}</div>
                          {opt.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                          )}
                        </>
                      )}
                    </div>
                    {opt.value === value && (
                      <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                    )}
                  </div>
                </Button>
              ))}
          </div>
          <div className="flex items-center justify-end p-2">
            {enableRecents && recentKey && (
              <Button
                type="button"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                try { clearRecents(recentKey); } catch {}
                setRecentsNonce((n) => n + 1);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear recents
              </Button>
            )}
          </div>
        </div>
      </CentralizedDrawer>
    </>
  );
}
